<template>
  <div>
    <BlogHeader v-if="!isFromCoconalaApp" />
    <div class="c-blog">
      <BlogContent />
      <!-- トップページへのリンクバナー -->
      <component
        :is="isFromCoconalaApp ? 'a' : 'NuxtLink'"
        to="/?ref=blog_detail"
        href="/?ref=blog_detail"
        class="c-blog_searchServiceBanner"
      >
        <img
          class="c-blog_searchServiceBannerImage"
          :src="$coconala.fileURL('/staticfiles/images/pages/blogs/search_service_banner_202104.png?20210419')"
          alt="サービス数40万件のスキルマーケット、あなたにぴったりのサービスを探す"
          loading="lazy"
          decoding="async"
        >
      </component>
      <!-- おすすめ記事一覧 -->
      <ClientOnly>
        <BlogRecommendedList :blog-id="Number($route.params.blogId)" />
      </ClientOnly>
    </div>
    <!-- v-ifで出し分けることでコンポーネントのキャッシュを破棄する -->
    <div v-if="isPaymentInProgress || isPaymentViewModal">
      <!-- 購入モーダルは直接ランディングさせないため -->
      <ClientOnly>
        <NuxtChild
          keep-alive
          :key="$route.name"
          @open-close-alert="openCloseAlert"
          @stop-payment="stopPayment"
        />
        <BlogPaymentCloseAlert
          class="c-blogPaymentCloseAlert"
          :is-visible="isVisibleCloseAlert"
          @close="isVisibleCloseAlert = false"
          @stop-payment="stopPayment"
        />
        <div class="c-blogPaymentNotification">
          <notifications
            group="blogPayment"
            position="top center"
          />
        </div>
      </ClientOnly>
    </div>
    <ClientOnly>
      <BlogPostPopup
        :is-visible="isVisiblePostPopup"
        :title="`ココナラでブログを投稿しました！\n${blog.title}｜${user.name}\n`"
        :path="`/blogs/${user.id}/${blog.id}`"
        :is-first-open="isFirstOpen"
        :hashtags="getSocialSharingTags()"
        @close="isVisiblePostPopup = false"
      />
    </ClientOnly>
    <TheVueport />
  </div>
</template>

<script>
import Promise from 'bluebird'
Promise.config({ cancellation: true })

import BlogContent from '~/components/organisms/BlogContent'
import BlogRecommendedList from '~/components/organisms/BlogRecommendedList'
import BlogPostPopup from '~/components/organisms/BlogPostPopup'
import TheVueport from '~/components/organisms/TheVueport'

// vuex
import { mapGetters, mapState } from 'vuex'

const MAX_DESCRIPTION = 280

import defaultOgpImage from '@/assets/images/common/ogp_large.png'

export default {
  scrollToTop: false,
  components: {
    BlogContent,
    BlogRecommendedList,
    BlogPostPopup,
    TheVueport,
    BlogHeader: () =>
      import('~/components/organisms/BlogHeader' /* webpackChunkName: "BlogHeader" */),
    BlogPaymentCloseAlert: () =>
      import('~/components/organisms/BlogPaymentCloseAlert' /* webpackChunkName: "BlogPaymentCloseAlert" */)
  },
  head() {
    return {
      htmlAttrs: {
        prefix: 'og: http://ogp.me/ns#'
      },
      __dangerouslyDisableSanitizers: ['script'],
      script: [
        {
          hid: 'json-ld-blog-posting',
          type: 'application/ld+json',
          innerHTML: this.$util.escapeBreadcrumb(this.jsonLdBlogPosting)
        },
        {
          hid: 'json-ld-breadcrumb',
          type: 'application/ld+json',
          innerHTML: this.$util.escapeBreadcrumb(this.jsonLdBreadcrumb)
        }
      ],
      title: this.metaTitle,
      link: [
        {
          rel: 'canonical',
          href: this.$coconala.url(this.$route.path)
        }
      ],
      meta: this.meta
    }
  },
  created() {
    this.$store.dispatch(
      'pages/blogs/user/recommendBlog/fetchRecommendBlog',
      this.$route.params.blogId
    )
  },
  mounted() {
    // 決済モーダル離脱防止処理
    window.addEventListener('beforeunload', this.preventSecession)
    if (this.isOwner) {
      // 投稿者のブログ投稿後の処理
      this.afterPublishing()
    } else {
      // 外部決済からの戻りを確認し、成功・失敗のモーダルへ遷移させる
      this.backExternalSettlement()
    }
  },
  async middleware({ app, store, params, route, error }) {
    const { fullPath } = route
    const isPaymentViewModal = route.meta.some(meta => meta.isPaymentViewModal)
    store.commit('pages/blogs/user/blog/SET_IS_PAYMENT_VIEW_MODAL', isPaymentViewModal)
    const promises = [
      new Promise((resolve, reject) => {
        store
          .dispatch('pages/users/user/fetchUser', {
            userId: params.id
          })
          .then(resolve)
          .catch(reject)
      }),
      new Promise((resolve, reject) => {
        store
          .dispatch('pages/blogs/user/blog/fetchBlog', {
            id: params.blogId,
            fullPath
          })
          .then(resolve)
          .catch(reject)
      }),
      new Promise((resolve, reject) => {
        store
          .dispatch('pages/blogs/user/services/fetchServices', params.id)
          .then(resolve)
          .catch(reject)
      })
    ]

    // ログイン済みの場合だけブログオーダー情報を取得しにいく
    if (store.getters['auth/isLoggedIn']) {
      promises.push(
        new Promise((resolve, reject) => {
          store
            .dispatch('pages/blogs/order/fetchBlogOrder', params.blogId)
            .then(resolve)
            .catch(reject)
        })
      )
    }

    try {
      await Promise.all(promises)
    } catch (err) {
      promises.forEach(p => p.cancel())
      const statusCode = app.$util.grpcCodeToHttpStatusCode(err.grpcCode)
      return error({
        statusCode,
        message: '',
        grpcCode: err.grpcCode
      })
    }

    // 記事がないOR別ユーザーの記事の場合
    if (
      !store.state.pages.blogs.user.blog.blog ||
      store.state.pages.blogs.user.blog.blog.userId != params.id
    ) {
      return error({ statusCode: 404 })
    }
  },
  data() {
    return {
      isVisibleCloseAlert: false,
      isVisiblePostPopup: false,
      isFirstOpen: false
    }
  },
  methods: {
    preventSecession(e) {
      if (this.isPaymentInProgress) {
        e.preventDefault()
        e.returnValue = 'このサイトを離れますか？'
      }
    },
    stopPayment() {
      this.isVisibleCloseAlert = false
      // 決済中フラグをfalseにする
      this.$store.commit('pages/blogs/user/blog/SET_IS_PAYMENT_IN_PROGRESS', false)
      if (this.isReplace) {
        this.$router.replace({ name: 'blogs-id-blogId' }, () => {})
      } else {
        this.$router.push({ name: 'blogs-id-blogId' }, () => {})
      }
    },
    openCloseAlert(isReplace = false) {
      this.isReplace = isReplace
      this.isVisibleCloseAlert = true
    },
    backExternalSettlement() {
      const status = this.$util.castToBooleanQueryString(this.$route.query.status)
      const carrierType = this.$util.castToBooleanQueryString(this.$route.query.carrierType)
      const carrierCompleteError = this.$util.castToBooleanQueryString(
        this.$route.query.carrierCompleteError
      )
      if (status && carrierType) {
        // レアケースだが、決済完了APIで失敗した場合はトーストで通知する
        if (carrierCompleteError) {
          this.$nextTick(() => {
            this.$notify({
              group: 'flash',
              title: '決済に失敗しました',
              text: 'お問い合わせください',
              type: 'error'
            })
          })
          return
        }
        if (
          status ===
          this.$store.state.pages.blogs.constants.CALL_BACK.MOBILE_CARRIERS.STATUS.SUCCESS
        ) {
          this.$router.replace({ name: 'blogs-id-blogId-complete' }, () => {})
        } else {
          // 決済エラーを表示させる
          // モーダルが開くので決済中フラグを立てる
          this.$store.commit('pages/blogs/user/blog/SET_IS_PAYMENT_IN_PROGRESS', true)
          this.$router.replace(
            {
              name: 'blogs-id-blogId-input',
              params: {
                carrierError: true,
                carrierErrorMsg: '決済に失敗しました',
                carrierType
              }
            },
            () => {}
          )
        }
      }
    },
    afterPublishing() {
      if (this.$route.params.afterPost) {
        this.isVisiblePostPopup = true
        this.isFirstOpen = this.$route.params.isFirstOpen
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['isFromCoconalaApp']),
    ...mapGetters('pages/blogs/user/blog', ['getSocialSharingTags']),
    ...mapGetters('pages/users', ['isOwner']),
    ...mapState('pages/blogs/user/blog', ['blog', 'isPaymentViewModal', 'isPaymentInProgress']),
    ...mapState('pages/blogs/order', ['blogOrder']),
    ...mapState('pages/users', ['user']),
    ...mapState('pages/blogs/constants', ['ORDER_STATUS']),
    description() {
      return this.blog.summary.substring(0, MAX_DESCRIPTION)
    },
    meta() {
      const ogpUrl = this.$coconala.url(this.$route.path)
      const meta = [
        {
          hid: 'description',
          name: 'description',
          content: this.description
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: `ブログ,${this.user.name},マーケットプレイス,ココナラ,coconala,ここなら`
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.metaTitle
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'article'
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.description
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: ogpUrl
        },
        // 以下、TwitterOGP
        {
          hid: 'twitter:card',
          property: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          hid: 'twitter:url',
          property: 'twitter:url',
          content: ogpUrl
        },
        {
          hid: 'twitter:title',
          property: 'twitter:title',
          content: this.metaTitle
        },
        {
          hid: 'twitter:description',
          property: 'twitter:description',
          content: this.description
        }
      ]
      meta.push({
        hid: 'og:image',
        property: 'og:image',
        content: this.blog.coverImageUrl ? this.blog.coverImageUrl : defaultOgpImage
      })
      return meta
    },
    metaTitle() {
      return `${this.blog.title}｜${this.user.name}｜coconalaブログ`
    },
    jsonLdBlogPosting() {
      const jsonLd = {
        '@context': 'http://schema.org/',
        '@type': 'BlogPosting',
        mainEntityOfPage: this.$route.path,
        headline: this.blog.title,
        keywords: this.blog.blogTagsList.map(i => i.name),
        datePublished: this.$util.dayjs.unix(this.blog.openedAt).format(),
        dateModified: this.$util.dayjs.unix(this.blog.updatedAt).format(),
        author: {
          '@type': 'Person',
          name: this.user.name,
          url: this.$coconala.url(`/users/${this.user.id}`)
        },
        publisher: {
          '@type': 'Organization',
          name: 'ココナラ',
          logo: {
            '@type': 'ImageObject',
            url: 'https://coconala.com/images/facebook.png',
            width: 250,
            height: 250
          }
        },
        description: this.description
      }
      if (this.blog.coverImageUrl) {
        jsonLd['image'] = {
          '@type': 'ImageObject',
          url: this.blog.coverImageUrl,
          width: 630,
          height: 354
        }
      } else {
        jsonLd['image'] = {
          '@type': 'ImageObject',
          url: this.$coconala.userIconURLV2(this.user.resizableProfileIcon, 200, 200),
          width: 200,
          height: 200
        }
      }
      return jsonLd
    },
    jsonLdBreadcrumb() {
      return {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': this.$coconala.url('/'),
              name: 'ココナラTOP'
            }
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': this.$coconala.url(`/blogs/${this.user.id}`),
              name: `${this.user.name}ブログ一覧`
            }
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@id': this.$coconala.url(this.$route.path),
              name: this.blog.title
            }
          }
        ]
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    // 決済モーダルが開いている場合は何もしない
    if (this.isPaymentViewModal) {
      return next()
    }
    // 詳細画面で、決済中フラグが立っている場合は離脱防止する
    if (this.isPaymentInProgress) {
      const answer = window.confirm(
        'このページから移動しますか？\n移動した場合、入力情報が保存されない可能性があります。'
      )
      if (answer) {
        // 決済中フラグをfalseにする
        this.$store.commit('pages/blogs/user/blog/SET_IS_PAYMENT_IN_PROGRESS', false)
        return next()
      } else {
        return next(false)
      }
    }
    return next()
  },
  destroyed() {
    window.removeEventListener('beforeunload', this.preventSecession)
  }
}
</script>

<style lang="scss" scoped>
.c-blogPaymentNotification {
  /deep/ .vue-notification-group {
    left: calc(50% - 280px) !important;
    z-index: 9000;
    margin: 0 5px 5px;
    padding: 10px;
    width: 560px !important;

    .notification-title {
      text-align: center;
    }

    .notification-content {
      text-align: center;
    }

    &.error {
      border-left-color: #b82e24;
      background: #e54d42;
    }
  }
}

.c-blog {
  margin: 32px auto;
  width: 630px;

  &_searchServiceBanner {
    position: relative;
    display: block;
    margin-bottom: 32px;
    padding-top: calc(100% * 11 / 63);
    width: 100%;
  }
  &_searchServiceBannerImage {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.c-blogPaymentCloseAlert {
  /deep/ .c-modal {
    &_body {
      padding-bottom: 20px;
    }
  }

  /deep/ .modal-content {
    max-width: 340px !important;
  }
}

@media (max-width: $breakpoint-m) {
  .c-blogPaymentNotification {
    /deep/ .vue-notification-group {
      left: calc(50% - 48%) !important;
      width: 96% !important;
    }
  }

  .c-blog {
    margin: 0 auto 26px;
    width: 100%;
    &_searchServiceBanner {
      margin: 0 16px 24px;
      width: calc(100% - 32px);
    }
  }
}
</style>
